/**
 * IT Support Tickets Component
 * Phase 2 of HR & Support Module
 * 
 * Features:
 * - Create and view tickets
 * - Add comments
 * - Upload attachments (including screenshots/recordings)
 * - Track ticket status
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Ticket,
  Plus,
  Search,
  Filter,
  Clock,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  Paperclip,
  Upload,
  X,
  ChevronRight,
  Camera,
  Video,
  Loader2,
  FileText,
  Image,
  Film,
  Download,
  Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import ScreenCapture from './ScreenCapture';
import ConfirmActionModal from '../it/ConfirmActionModal';
import { ITKpiRow, TicketRow, TicketSkeleton, TicketsEmpty } from './it-support/ITSupportUi';
import { formatDateOnly, formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PRIORITY_CONFIG = {
  low: { label: 'Low', color: 'bg-gray-100 text-gray-700' },
  medium: { label: 'Medium', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700' },
  urgent: { label: 'Urgent', color: 'bg-red-100 text-red-700' }
};

const STATUS_CONFIG = {
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700', icon: Clock },
  in_progress: { label: 'Pending', color: 'bg-indigo-100 text-indigo-700', icon: Loader2 },
  waiting_on_user: { label: 'Waiting on User', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  closed: { label: 'Closed', color: 'bg-gray-100 text-gray-700', icon: CheckCircle }
};

const CATEGORY_OPTIONS = [
  { value: 'hardware', label: 'Hardware' },
  { value: 'software', label: 'Software' },
  { value: 'network', label: 'Network' },
  { value: 'access', label: 'Access/Permissions' },
  { value: 'email', label: 'Email' },
  { value: 'printer', label: 'Printer' },
  { value: 'other', label: 'Other' }
];

const MAX_FILE_SIZE_MB = 100;

const ITTickets = ({ currentUser }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState(null);
  // IT-SUPPORT-UI-001: client-side refinements over the already-loaded list (status stays server-side as before)
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Create form state
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    category: 'software',
    priority: 'medium'
  });
  const [creating, setCreating] = useState(false);

  // Comment state
  const [newComment, setNewComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  // Attachment state
  const [uploadingFile, setUploadingFile] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);

  // Status update state
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Check if current user can manage tickets (Admin/IT Team)
  const canManageTickets = currentUser?.role === 'admin' || 
                           currentUser?.role === 'it_support' || 
                           currentUser?.role === 'it_manager';

  const fetchTickets = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/api/it-tickets/my-tickets`, { params });
      setTickets(response.data.tickets || []);
    } catch (err) {
      console.error('Error fetching tickets:', err);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/api/it-tickets/stats/summary`);
      setStats(response.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchTicketDetails = useCallback(async (ticketId) => {
    try {
      const response = await axios.get(`${API}/api/it-tickets/${ticketId}`);
      setSelectedTicket(response.data);
    } catch (err) {
      console.error('Error fetching ticket details:', err);
      toast.error('Failed to load ticket details');
    }
  }, []);

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, [fetchTickets, fetchStats]);

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    
    if (!createForm.title.trim() || !createForm.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setCreating(true);
      const response = await axios.post(`${API}/api/it-tickets`, createForm);
      
      toast.success(`Ticket ${response.data.ticket_number} created successfully`);
      setShowCreateModal(false);
      setCreateForm({ title: '', description: '', category: 'software', priority: 'medium' });
      
      // Upload any pending files
      if (pendingFiles.length > 0 && response.data.ticket_id) {
        for (const file of pendingFiles) {
          await uploadAttachment(response.data.ticket_id, file);
        }
        setPendingFiles([]);
      }
      
      fetchTickets();
      fetchStats();
    } catch (err) {
      console.error('Error creating ticket:', err);
      toast.error(err.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedTicket) return;

    try {
      setAddingComment(true);
      await axios.post(`${API}/api/it-tickets/${selectedTicket.id}/comments`, {
        content: newComment,
        is_internal: false
      });
      
      toast.success('Comment added');
      setNewComment('');
      fetchTicketDetails(selectedTicket.id);
    } catch (err) {
      console.error('Error adding comment:', err);
      toast.error('Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const uploadAttachment = async (ticketId, file) => {
    // Validate file size
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      toast.error(`File "${file.name}" exceeds ${MAX_FILE_SIZE_MB}MB limit`);
      return false;
    }

    try {
      setUploadingFile(true);
      
      // Use proxy upload to avoid CORS issues with S3
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/api/it-tickets/${ticketId}/attachments/proxy-upload`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        toast.success(`"${file.name}" uploaded successfully`);
        
        if (selectedTicket?.id === ticketId) {
          fetchTicketDetails(ticketId);
        }
        
        return true;
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Error uploading file:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Upload failed';
      toast.error(`Failed to upload "${file.name}": ${errorMessage}`);
      return false;
    } finally {
      setUploadingFile(false);
    }
  };

  const handleFileSelect = async (e, ticketId = null) => {
    const files = Array.from(e.target.files);
    
    if (ticketId) {
      // Upload to existing ticket
      for (const file of files) {
        await uploadAttachment(ticketId, file);
      }
    } else {
      // Store for new ticket
      setPendingFiles(prev => [...prev, ...files]);
    }
    
    e.target.value = '';
  };

  const handleDeleteAttachment = (ticketId, attachmentId, filename) => setConfirmAction({
    title: 'Delete Attachment?',
    message: `"${filename}" will be permanently removed from this ticket.`,
    confirmLabel: 'Delete Attachment',
    run: async () => {
      await axios.delete(`${API}/api/it-tickets/${ticketId}/attachments/${attachmentId}`);
      toast.success('Attachment deleted');
      fetchTicketDetails(ticketId);
    },
  });

  // Update ticket status (Admin/IT team only)
  const handleStatusChange = async (ticketId, newStatus, resolutionNotes = '') => {
    try {
      setUpdatingStatus(true);
      const data = { status: newStatus };
      if (newStatus === 'resolved' || newStatus === 'closed') {
        if (resolutionNotes) {
          data.resolution_notes = resolutionNotes;
        }
      }
      
      await axios.put(`${API}/api/it-tickets/${ticketId}`, data);
      toast.success(`Ticket ${newStatus === 'resolved' ? 'resolved' : newStatus === 'closed' ? 'closed' : 'updated'} successfully`);
      
      // Close the modal and return to dashboard
      setSelectedTicket(null);
      
      // Refresh the tickets list and stats
      fetchTickets();
      fetchStats();
    } catch (err) {
      console.error('Error updating status:', err);
      const message = err.response?.data?.detail || 'Failed to update status';
      toast.error(message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDownloadAttachment = async (ticketId, attachmentId) => {
    try {
      const response = await axios.get(
        `${API}/api/it-tickets/${ticketId}/attachments/${attachmentId}/download`
      );
      
      // Open download URL in new tab
      window.open(response.data.download_url, '_blank');
    } catch (err) {
      console.error('Error getting download URL:', err);
      toast.error('Failed to download file');
    }
  };

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case 'image': return Image;
      case 'video': return Film;
      case 'pdf': return FileText;
      default: return Paperclip;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const q = search.trim().toLowerCase();
  const visibleTickets = tickets.filter(t =>
    (priorityFilter === 'all' || t.priority === priorityFilter) &&
    (categoryFilter === 'all' || t.category === categoryFilter) &&
    (!q || [t.ticket_number, t.title, t.description, t.category].some(v => (v || '').toLowerCase().includes(q))));
  const filtersActive = !!q || statusFilter !== 'all' || priorityFilter !== 'all' || categoryFilter !== 'all';
  const clearFilters = () => { setSearch(''); setStatusFilter('all'); setPriorityFilter('all'); setCategoryFilter('all'); };
  const selectCls = 'lv-input h-10 rounded-xl border border-slate-200 bg-white text-[13px] font-medium text-slate-700 shadow-none focus:ring-0';

  return (
    <div className="space-y-3.5" data-testid="it-support-root">
      {/* KPIs — live stats; click toggles the status filter */}
      <ITKpiRow stats={stats} activeStatus={statusFilter} onPick={setStatusFilter} />

      <div className="lv-card an-rise p-5" style={{ animationDelay: '160ms' }} data-testid="it-tickets-card">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-[20px] font-bold text-slate-900 tracking-tight leading-tight">IT Support Tickets</h2>
            <p className="text-[12.5px] text-slate-500 mt-0.5">Create and track your IT support requests</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="an-btn an-btn-primary h-10 px-4 rounded-xl gap-2 text-[13.5px] font-semibold" data-testid="it-new-ticket-btn">
            <Plus className="w-4 h-4" />
            New Ticket
          </Button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-2.5 mb-4" data-testid="it-toolbar">
          <div className="relative flex-1 min-w-0">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search tickets by subject, ID, or keywords..." data-testid="it-search-input"
              className="lv-input w-full h-10 rounded-xl border border-slate-200 bg-white pl-10 pr-3 text-[13.5px] text-slate-800 placeholder:text-slate-400" />
          </div>
          <div className="flex flex-wrap items-center gap-2.5">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={`${selectCls} w-[150px]`} data-testid="it-status-filter"><Filter className="w-4 h-4 mr-2 text-slate-500" /><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Status</SelectItem>{Object.entries(STATUS_CONFIG).map(([value, config]) => <SelectItem key={value} value={value}>{config.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger className={`${selectCls} w-[150px]`} data-testid="it-priority-filter"><AlertCircle className="w-4 h-4 mr-2 text-slate-500" /><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Priority</SelectItem>{Object.entries(PRIORITY_CONFIG).map(([value, config]) => <SelectItem key={value} value={value}>{config.label}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className={`${selectCls} w-[170px]`} data-testid="it-category-filter"><Ticket className="w-4 h-4 mr-2 text-slate-500" /><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All Categories</SelectItem>{CATEGORY_OPTIONS.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}</SelectContent>
            </Select>
            <button type="button" onClick={clearFilters} disabled={!filtersActive} data-testid="it-clear-filters-btn"
              className={`an-btn h-10 px-4 rounded-xl border text-[13px] font-semibold transition-opacity ${filtersActive ? 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50' : 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'}`}>Clear</button>
          </div>
        </div>

        {/* Tickets */}
        {loading ? <TicketSkeleton />
          : visibleTickets.length === 0 ? <TicketsEmpty filtered={filtersActive} onNew={() => setShowCreateModal(true)} onClear={clearFilters} />
          : (
            <div key={`${statusFilter}-${priorityFilter}-${categoryFilter}-${q}`} className="space-y-2.5" data-testid="it-tickets-list">
              {visibleTickets.map((ticket, i) => <TicketRow key={ticket.id} ticket={ticket} index={i} onOpen={() => fetchTicketDetails(ticket.id)} />)}
            </div>
          )}
      </div>

      {/* Create Ticket Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
            <DialogDescription>Describe your issue and we'll help you resolve it.</DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleCreateTicket} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Title *</label>
              <Input
                placeholder="Brief description of the issue"
                value={createForm.title}
                onChange={(e) => setCreateForm(prev => ({ ...prev, title: e.target.value }))}
                required
                minLength={5}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">Category</label>
                <Select
                  value={createForm.category}
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">Priority</label>
                <Select
                  value={createForm.priority}
                  onValueChange={(v) => setCreateForm(prev => ({ ...prev, priority: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(PRIORITY_CONFIG).map(([value, config]) => (
                      <SelectItem key={value} value={value}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-gray-700">Description *</label>
              <Textarea
                placeholder="Provide detailed information about your issue..."
                value={createForm.description}
                onChange={(e) => setCreateForm(prev => ({ ...prev, description: e.target.value }))}
                required
                minLength={10}
                rows={4}
              />
            </div>

            {/* Pending files preview */}
            {pendingFiles.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Attachments</label>
                <div className="flex flex-wrap gap-2">
                  {pendingFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded text-sm">
                      <Paperclip className="w-3 h-3" />
                      <span className="truncate max-w-32">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                        className="text-gray-400 hover:text-red-500"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="file"
                id="create-file-input"
                className="hidden"
                multiple
                onChange={(e) => handleFileSelect(e)}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('create-file-input').click()}
              >
                <Paperclip className="w-4 h-4 mr-2" />
                Attach Files
              </Button>
              
              {/* Screen Capture */}
              <ScreenCapture 
                onCapture={(file) => setPendingFiles(prev => [...prev, file])}
                disabled={creating}
              />
              
              <span className="text-xs text-gray-500 w-full mt-2">Max {MAX_FILE_SIZE_MB}MB per file</span>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create Ticket
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Ticket Details Modal */}
      <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedTicket && (
            <>
              <DialogHeader>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-mono text-gray-500">{selectedTicket.ticket_number}</span>
                  
                  {/* Status Badge with Change Option for Admin/IT */}
                  {canManageTickets ? (
                    <Select 
                      value={selectedTicket.status} 
                      onValueChange={(newStatus) => handleStatusChange(selectedTicket.id, newStatus)}
                      disabled={updatingStatus}
                    >
                      <SelectTrigger 
                        className={`w-auto h-7 px-2 text-xs font-medium border-0 ${STATUS_CONFIG[selectedTicket.status]?.color}`}
                        data-testid="ticket-status-dropdown"
                      >
                        {updatingStatus ? (
                          <Loader2 className="w-3 h-3 animate-spin mr-1" />
                        ) : null}
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_CONFIG).map(([value, config]) => {
                          const StatusIcon = config.icon;
                          return (
                            <SelectItem key={value} value={value}>
                              <div className="flex items-center gap-2">
                                <StatusIcon className="w-3 h-3" />
                                {config.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={STATUS_CONFIG[selectedTicket.status]?.color}>
                      {STATUS_CONFIG[selectedTicket.status]?.label}
                    </Badge>
                  )}
                  
                  <Badge className={PRIORITY_CONFIG[selectedTicket.priority]?.color}>
                    {PRIORITY_CONFIG[selectedTicket.priority]?.label}
                  </Badge>
                </div>
                <DialogTitle className="text-lg">{selectedTicket.title}</DialogTitle>
              </DialogHeader>

              <div className="space-y-6">
                {/* Description */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Description</h4>
                  <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 p-3 rounded-lg text-sm">
                    {selectedTicket.description}
                  </p>
                </div>

                {/* Meta info */}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Category:</span>
                    <span className="ml-2 capitalize">{selectedTicket.category}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Created:</span>
                    <span className="ml-2">{formatDateTimeLocal(selectedTicket.created_at)}</span>
                  </div>
                  {selectedTicket.assigned_to_name && (
                    <div>
                      <span className="text-gray-500">Assigned to:</span>
                      <span className="ml-2">{selectedTicket.assigned_to_name}</span>
                    </div>
                  )}
                  {selectedTicket.resolved_at && (
                    <div>
                      <span className="text-gray-500">Resolved:</span>
                      <span className="ml-2">{formatDateTimeLocal(selectedTicket.resolved_at)}</span>
                    </div>
                  )}
                </div>

                {/* Resolution notes */}
                {selectedTicket.resolution_notes && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="text-sm font-medium text-green-800 mb-1">Resolution</h4>
                    <p className="text-sm text-green-700">{selectedTicket.resolution_notes}</p>
                  </div>
                )}

                {/* Attachments */}
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <h4 className="text-sm font-medium text-gray-700">Attachments</h4>
                    <div className="flex flex-wrap gap-2">
                      <input
                        type="file"
                        id="detail-file-input"
                        className="hidden"
                        multiple
                        onChange={(e) => handleFileSelect(e, selectedTicket.id)}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('detail-file-input').click()}
                        disabled={uploadingFile}
                      >
                        {uploadingFile ? (
                          <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Add File
                      </Button>
                      <ScreenCapture 
                        onCapture={(file) => uploadAttachment(selectedTicket.id, file)}
                        disabled={uploadingFile}
                      />
                    </div>
                  </div>
                  
                  {selectedTicket.attachments?.filter(a => ['completed', 'uploaded', 'pending'].includes(a.upload_status)).length > 0 ? (
                    <div className="space-y-2">
                      {selectedTicket.attachments
                        .filter(a => ['completed', 'uploaded', 'pending'].includes(a.upload_status))
                        .map((attachment) => {
                          const FileIcon = getFileIcon(attachment.file_type);
                          return (
                            <div
                              key={attachment.id}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <FileIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                                <span className="text-sm truncate">{attachment.filename}</span>
                                <span className="text-xs text-gray-400">
                                  {formatFileSize(attachment.file_size)}
                                </span>
                              </div>
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownloadAttachment(selectedTicket.id, attachment.id)}
                                >
                                  <Download className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAttachment(selectedTicket.id, attachment.id, attachment.filename)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">No attachments</p>
                  )}
                </div>

                {/* Comments */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Comments</h4>
                  
                  {selectedTicket.comments?.length > 0 ? (
                    <div className="space-y-3 mb-4">
                      {selectedTicket.comments.map((comment) => (
                        <div
                          key={comment.id}
                          className={`p-3 rounded-lg ${comment.is_internal ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'}`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium text-gray-900">
                              {comment.created_by_name}
                            </span>
                            <span className="text-xs text-gray-400">
                              {formatDateTimeLocal(comment.created_at)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 whitespace-pre-wrap">{comment.content}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic mb-4">No comments yet</p>
                  )}

                  {/* Add comment */}
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Add a comment..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={2}
                      className="flex-1"
                    />
                    <Button
                      onClick={handleAddComment}
                      disabled={!newComment.trim() || addingComment}
                    >
                      {addingComment ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send'}
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* NO-NATIVE-POPUPS-001 — TitleFlow confirmation modal */}
      <ConfirmActionModal
        open={!!confirmAction}
        title={confirmAction?.title}
        message={confirmAction?.message}
        confirmLabel={confirmAction?.confirmLabel}
        tone="danger"
        theme="light"
        onCancel={() => setConfirmAction(null)}
        onConfirm={async () => {
          try {
            await confirmAction.run();
          } catch (err) {
            toast.error(err.message || 'Action failed');
          }
          setConfirmAction(null);
        }}
      />
    </div>
  );
};

export default ITTickets;
